import { DEFAULT_ACTIVE_PRESET_ID, DEFAULT_PRESETS } from './defaultPresets';
import { MAX_SCALE, MIN_SCALE } from './scaleLimits';
import { generatePresetId } from './presetUtils';
import {
  normalizePresetLayerLocks,
  normalizePresetLayerOrder,
} from './presetLayerUtils';
import { sanitizeAutoSizeBinding } from './primitiveAutoSizeBindingUtils';
import { sanitizeTextLayerFontFamily } from '../fonts/textLayerFonts';
import {
  normalizePrimitiveWithResetSnapshot,
  stampPrimitiveResetSnapshot,
} from './primitiveResetSnapshot';

export const PRESET_STORAGE_KEY = 'lowerThirds.presets.v1';
export const PRESET_STORAGE_VERSION = 1;

const styleSet = new Set(DEFAULT_PRESETS.map((preset) => preset.preset));
const colorPattern = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const primitiveTypeSet = new Set(['line', 'rect', 'circle', 'roundedRect', 'parallelogram', 'image']);
const primitiveColorRoleSet = new Set(['primary', 'secondary', 'textUpper', 'textLower']);
const primitiveColorModeSet = new Set(['theme', 'custom']);
const primitiveLayerKindSet = new Set(['shape', 'text', 'image']);
const primitiveTextSourceSet = new Set(['main', 'sub', 'generic']);
const templateFamilySet = new Set(['classic-slide', 'minimal-box', 'modern-accent', 'edge-link', 'logo-badge']);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const sanitizeColor = (value, fallback) => (
  typeof value === 'string' && colorPattern.test(value) ? value : fallback
);

const sanitizeNumber = (value, fallback, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return clamp(numeric, min, max);
};
const sanitizeBoolean = (value, fallback) => (
  typeof value === 'boolean' ? value : fallback
);

const sanitizePrimitiveColorMode = (value, fallback = 'theme') => (
  primitiveColorModeSet.has(value) ? value : fallback
);

const sanitizePrimitiveColorRole = (value, fallback = 'primary') => (
  primitiveColorRoleSet.has(value) ? value : fallback
);

const sanitizePrimitiveLayerKind = (value, fallback = 'shape') => (
  primitiveLayerKindSet.has(value) ? value : fallback
);

const sanitizePrimitiveTextSource = (value, fallback = 'main') => (
  primitiveTextSourceSet.has(value) ? value : fallback
);

const sanitizePrimitiveTextValue = (value) => (
  typeof value === 'string' ? value : ''
);

const sanitizePrimitiveLayerName = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const withSanitizedPrimitiveResetFields = (primitive, rawPrimitive, {
  minWidth = 8,
  minHeight = 1,
  maxCornerRadius = 999,
  allowSkew = false,
} = {}) => ({
  ...primitive,
  resetX: sanitizeNumber(rawPrimitive?.resetX, primitive.x, -1920, 1920),
  resetY: sanitizeNumber(rawPrimitive?.resetY, primitive.y, -1080, 1080),
  resetWidth: sanitizeNumber(rawPrimitive?.resetWidth, primitive.width, minWidth, 1920),
  resetHeight: sanitizeNumber(rawPrimitive?.resetHeight, primitive.height, minHeight, 1080),
  resetRotation: sanitizeNumber(rawPrimitive?.resetRotation, primitive.rotation, -180, 180),
  resetStrokeWidth: sanitizeNumber(rawPrimitive?.resetStrokeWidth, primitive.strokeWidth, 1, 80),
  resetCornerRadius: sanitizeNumber(rawPrimitive?.resetCornerRadius, primitive.cornerRadius || 0, 0, maxCornerRadius),
  resetSkewX: allowSkew
    ? sanitizeNumber(rawPrimitive?.resetSkewX, primitive.skewX || 0, -1920, 1920)
    : 0,
});

const sanitizePrimitive = (rawPrimitive, index) => {
  const type = primitiveTypeSet.has(rawPrimitive?.type) ? rawPrimitive.type : null;
  if (!type) return null;

  const id = typeof rawPrimitive?.id === 'string' && rawPrimitive.id.trim()
    ? rawPrimitive.id.trim()
    : `shape-${Date.now()}-${index}-${Math.floor(Math.random() * 1000000)}`;
  const baseColorRole = sanitizePrimitiveColorRole(rawPrimitive?.colorRole, 'primary');
  const fillColorMode = sanitizePrimitiveColorMode(rawPrimitive?.fillColorMode, 'theme');
  const fillColorRole = sanitizePrimitiveColorRole(rawPrimitive?.fillColorRole, baseColorRole);
  const fillColorValue = sanitizeColor(rawPrimitive?.fillColorValue, '#ffffff');
  const strokeColorMode = sanitizePrimitiveColorMode(rawPrimitive?.strokeColorMode, 'theme');
  const strokeColorRole = sanitizePrimitiveColorRole(rawPrimitive?.strokeColorRole, baseColorRole);
  const strokeColorValue = sanitizeColor(rawPrimitive?.strokeColorValue, '#ffffff');
  const strokeWidth = sanitizeNumber(rawPrimitive?.strokeWidth, 3, 1, 80);
  const rotation = sanitizeNumber(rawPrimitive?.rotation, 0, -180, 180);
  const x = sanitizeNumber(rawPrimitive?.x, 40, -1920, 1920);
  const y = sanitizeNumber(rawPrimitive?.y, 40, -1080, 1080);
  const layerKind = sanitizePrimitiveLayerKind(rawPrimitive?.layerKind, 'shape');
  const textSource = sanitizePrimitiveTextSource(rawPrimitive?.textSource, 'main');
  const textPaddingX = sanitizeNumber(rawPrimitive?.textPaddingX, 0, 0, 1920);
  const textFontRatio = sanitizeNumber(rawPrimitive?.textFontRatio, textSource === 'main' ? 0.8 : 0.7, 0.2, 1.5);
  const textBaselineRatio = sanitizeNumber(rawPrimitive?.textBaselineRatio, 0.78, 0.2, 1.2);
  const textFontFamily = layerKind === 'text'
    ? sanitizeTextLayerFontFamily(rawPrimitive?.textFontFamily)
    : null;
  const textValue = sanitizePrimitiveTextValue(rawPrimitive?.textValue);
  const hideFromPeoplePanel = sanitizeBoolean(rawPrimitive?.hideFromPeoplePanel, false);
  const skewX = sanitizeNumber(rawPrimitive?.skewX, 0, -1920, 1920);
  const templateFamily = templateFamilySet.has(rawPrimitive?.templateFamily) ? rawPrimitive.templateFamily : null;
  const templatePart = typeof rawPrimitive?.templatePart === 'string' && rawPrimitive.templatePart.trim()
    ? rawPrimitive.templatePart.trim()
    : null;
  const autoSizeBinding = sanitizeAutoSizeBinding(rawPrimitive?.autoSizeBinding, {
    type,
    layerKind,
  });

  const commonFields = {
    colorRole: baseColorRole,
    fillColorMode,
    fillColorRole,
    fillColorValue,
    strokeColorMode,
    strokeColorRole,
    strokeColorValue,
    visible: sanitizeBoolean(rawPrimitive?.visible, true),
    mainTextVisible: sanitizeBoolean(rawPrimitive?.mainTextVisible, false),
    subTextVisible: sanitizeBoolean(rawPrimitive?.subTextVisible, false),
    layerKind,
    textSource,
    textPaddingX,
    textFontRatio,
    textBaselineRatio,
    textFontFamily,
    textValue,
    hideFromPeoplePanel,
    skewX: type === 'parallelogram' ? skewX : 0,
    templateFamily,
    templatePart,
    autoSizeBinding,
    layerName: sanitizePrimitiveLayerName(rawPrimitive?.layerName),
  };

  if (type === 'image') {
    if (typeof rawPrimitive?.imageData !== 'string' || !rawPrimitive.imageData.trim()) {
      return null;
    }
    const cropModes = new Set(['original', 'square', 'circle']);
    const imageCropMode = cropModes.has(rawPrimitive?.imageCropMode) ? rawPrimitive.imageCropMode : 'original';
    const width = sanitizeNumber(rawPrimitive?.width, 140, 8, 1920);
    const height = sanitizeNumber(rawPrimitive?.height, 140, 8, 1080);
    
    return normalizePrimitiveWithResetSnapshot(withSanitizedPrimitiveResetFields({
      id,
      type,
      x,
      y,
      width,
      height,
      rotation,
      strokeWidth,
      ...commonFields,
      fill: false,
      stroke: false,
      cornerRadius: 0,
      layerKind: 'image',
      imageData: rawPrimitive.imageData,
      imageCropMode,
      resetSnapshot: rawPrimitive?.resetSnapshot,
    }, rawPrimitive, {
      minWidth: 8,
      minHeight: 8,
      maxCornerRadius: 999,
      allowSkew: false,
    }));
  }

  if (type === 'circle') {
    const size = sanitizeNumber(rawPrimitive?.width ?? rawPrimitive?.height, 80, 8, 1200);
    return normalizePrimitiveWithResetSnapshot(withSanitizedPrimitiveResetFields({
      id,
      type,
      x,
      y,
      width: size,
      height: size,
      rotation,
      strokeWidth,
      ...commonFields,
      fill: layerKind === 'text' ? false : (rawPrimitive?.fill !== false),
      stroke: layerKind === 'text' ? false : Boolean(rawPrimitive?.stroke),
      cornerRadius: 0,
      resetSnapshot: rawPrimitive?.resetSnapshot,
    }, rawPrimitive, {
      minWidth: 8,
      minHeight: 8,
      maxCornerRadius: 0,
      allowSkew: false,
    }));
  }

  if (type === 'line') {
    return normalizePrimitiveWithResetSnapshot(withSanitizedPrimitiveResetFields({
      id,
      type,
      x,
      y,
      width: sanitizeNumber(rawPrimitive?.width, 220, 8, 1920),
      height: sanitizeNumber(rawPrimitive?.height, strokeWidth, 1, 200),
      rotation,
      strokeWidth,
      ...commonFields,
      fill: false,
      stroke: layerKind === 'text' ? false : true,
      cornerRadius: 0,
      resetSnapshot: rawPrimitive?.resetSnapshot,
    }, rawPrimitive, {
      minWidth: 8,
      minHeight: 1,
      maxCornerRadius: 0,
      allowSkew: false,
    }));
  }

  return normalizePrimitiveWithResetSnapshot(withSanitizedPrimitiveResetFields({
    id,
    type,
    x,
    y,
    width: sanitizeNumber(rawPrimitive?.width, 220, 8, 1920),
    height: sanitizeNumber(rawPrimitive?.height, 80, 8, 1080),
    rotation,
    strokeWidth,
    ...commonFields,
    fill: layerKind === 'text' ? false : (rawPrimitive?.fill !== false),
    stroke: layerKind === 'text' ? false : Boolean(rawPrimitive?.stroke),
    cornerRadius: type === 'roundedRect'
      ? sanitizeNumber(rawPrimitive?.cornerRadius, 16, 0, 540)
      : 0,
    resetSnapshot: rawPrimitive?.resetSnapshot,
  }, rawPrimitive, {
    minWidth: 8,
    minHeight: 8,
    maxCornerRadius: type === 'roundedRect' ? 540 : 0,
    allowSkew: type === 'parallelogram',
  }));
};

const sanitizeCustomPreset = (rawPreset) => {
  const fallback = DEFAULT_PRESETS[0];
  const id = typeof rawPreset?.id === 'string' && rawPreset.id.trim()
    ? rawPreset.id.trim()
    : generatePresetId();
  const shapePrimitives = Array.isArray(rawPreset?.shapePrimitives)
    ? rawPreset.shapePrimitives
      .map((shape, index) => sanitizePrimitive(shape, index))
      .filter(Boolean)
    : [];

  const rawLayerOrder = Array.isArray(rawPreset?.layerOrder) ? [...rawPreset.layerOrder] : [];
  const rawLayerLocks = rawPreset?.layerLocks && typeof rawPreset?.layerLocks === 'object' ? { ...rawPreset.layerLocks } : {};

  if (Boolean(rawPreset?.logoEnabled) && typeof rawPreset?.logoImageData === 'string' && rawPreset.logoImageData.startsWith('data:image/')) {
    const migratedId = `image-migrated-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const legacySize = sanitizeNumber(rawPreset?.logoSize, fallback.logoSize, 32, 640);
    const legacyX = sanitizeNumber(rawPreset?.logoOffsetX, fallback.logoOffsetX, -1920, 1920);
    const legacyY = sanitizeNumber(rawPreset?.logoOffsetY, fallback.logoOffsetY, -1080, 1080);
    const legacyRotation = sanitizeNumber(rawPreset?.logoRotation, fallback.logoRotation, -180, 180);

    const migratedImage = stampPrimitiveResetSnapshot({
      id: migratedId,
      type: 'image',
      layerKind: 'image',
      imageData: rawPreset.logoImageData,
      imageCropMode: 'square',
      x: legacyX,
      y: legacyY,
      width: legacySize,
      height: legacySize,
      rotation: legacyRotation,
      visible: true,
      fill: false,
      stroke: false,
      cornerRadius: 0,
      colorRole: 'primary',
      fillColorMode: 'theme',
      fillColorRole: 'primary',
      fillColorValue: '#ffffff',
      strokeColorMode: 'theme',
      strokeColorRole: 'primary',
      strokeColorValue: '#ffffff',
      mainTextVisible: false,
      subTextVisible: false,
      textSource: 'main',
      textPaddingX: 0,
      textFontRatio: 0.8,
      textBaselineRatio: 0.78,
      textValue: '',
      hideFromPeoplePanel: false,
      skewX: 0,
      templateFamily: null,
      templatePart: null,
      strokeWidth: 3,
      resetX: legacyX,
      resetY: legacyY,
      resetWidth: legacySize,
      resetHeight: legacySize,
      resetRotation: legacyRotation,
    }, { overwrite: true });
    shapePrimitives.push(migratedImage);
    
    // Replace 'logo' in layerOrder with the new primitive's layer ID
    const logoIndex = rawLayerOrder.indexOf('logo');
    if (logoIndex !== -1) {
      rawLayerOrder.splice(logoIndex, 1, `primitive:${migratedId}`);
    } else {
      rawLayerOrder.push(`primitive:${migratedId}`);
    }
    
    if (rawLayerLocks.logo) {
      rawLayerLocks[`primitive:${migratedId}`] = true;
      delete rawLayerLocks.logo;
    }
  }

  return {
    id,
    name: typeof rawPreset?.name === 'string' && rawPreset.name.trim()
      ? rawPreset.name.trim()
      : 'Custom Preset',
    isBuiltIn: false,
    preset: styleSet.has(rawPreset?.preset) ? rawPreset.preset : fallback.preset,
    colorPrimary: sanitizeColor(rawPreset?.colorPrimary, fallback.colorPrimary),
    colorSecondary: sanitizeColor(rawPreset?.colorSecondary, fallback.colorSecondary),
    colorTextUpper: sanitizeColor(rawPreset?.colorTextUpper, fallback.colorTextUpper),
    colorTextLower: sanitizeColor(rawPreset?.colorTextLower, fallback.colorTextLower),
    scale: sanitizeNumber(rawPreset?.scale, fallback.scale, MIN_SCALE, MAX_SCALE),
    rotation: sanitizeNumber(rawPreset?.rotation, fallback.rotation, -180, 180),
    posX: sanitizeNumber(rawPreset?.posX, fallback.posX, -1920, 1920),
    posY: sanitizeNumber(rawPreset?.posY, fallback.posY, -1080, 1080),
    classicRadius: sanitizeNumber(rawPreset?.classicRadius, fallback.classicRadius, 0, 60),
    classicAccentWidth: sanitizeNumber(rawPreset?.classicAccentWidth, fallback.classicAccentWidth, 0, 80),
    classicTextPaddingX: sanitizeNumber(rawPreset?.classicTextPaddingX, fallback.classicTextPaddingX, 10, 120),
    minimalNameHeight: sanitizeNumber(rawPreset?.minimalNameHeight, fallback.minimalNameHeight, 40, 140),
    minimalTitleHeight: sanitizeNumber(rawPreset?.minimalTitleHeight, fallback.minimalTitleHeight, 30, 120),
    minimalTextPaddingX: sanitizeNumber(rawPreset?.minimalTextPaddingX, fallback.minimalTextPaddingX, 10, 80),
    modernSkewOffset: sanitizeNumber(rawPreset?.modernSkewOffset, fallback.modernSkewOffset, 0, 120),
    modernAccentOffset: sanitizeNumber(rawPreset?.modernAccentOffset, fallback.modernAccentOffset, 0, 80),
    modernAccentWidth: sanitizeNumber(rawPreset?.modernAccentWidth, fallback.modernAccentWidth, 0, 80),
    modernTextX: sanitizeNumber(rawPreset?.modernTextX, fallback.modernTextX, 20, 200),
    templateShapeVisible: sanitizeBoolean(rawPreset?.templateShapeVisible, fallback.templateShapeVisible !== false),
    templateMainTextVisible: sanitizeBoolean(rawPreset?.templateMainTextVisible, fallback.templateMainTextVisible !== false),
    templateSubTextVisible: sanitizeBoolean(rawPreset?.templateSubTextVisible, fallback.templateSubTextVisible !== false),
    shapePrimitives,
    layerOrder: normalizePresetLayerOrder(rawLayerOrder, shapePrimitives),
    layerLocks: normalizePresetLayerLocks(rawLayerLocks, shapePrimitives),
    updatedAt: sanitizeNumber(rawPreset?.updatedAt, Date.now(), 0, Number.MAX_SAFE_INTEGER),
  };
};

export const loadPresetStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { activePresetId: DEFAULT_ACTIVE_PRESET_ID, customPresets: [] };
  }

  try {
    const raw = window.localStorage.getItem(PRESET_STORAGE_KEY);
    if (!raw) return { activePresetId: DEFAULT_ACTIVE_PRESET_ID, customPresets: [] };
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') {
      return { activePresetId: DEFAULT_ACTIVE_PRESET_ID, customPresets: [] };
    }

    const customSource = Array.isArray(parsed.customPresets) ? parsed.customPresets : [];
    const deduped = new Map();
    for (const rawPreset of customSource) {
      const preset = sanitizeCustomPreset(rawPreset);
      deduped.set(preset.id, preset);
    }
    const customPresets = [...deduped.values()];
    const activePresetId = typeof parsed.activePresetId === 'string' && parsed.activePresetId.trim()
      ? parsed.activePresetId.trim()
      : DEFAULT_ACTIVE_PRESET_ID;

    return { activePresetId, customPresets };
  } catch (error) {
    console.warn('[LowerThirds] Failed to parse preset storage; using defaults.', error);
    return { activePresetId: DEFAULT_ACTIVE_PRESET_ID, customPresets: [] };
  }
};

export const savePresetStorage = ({ activePresetId, customPresets }) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const sanitizedCustomPresets = Array.isArray(customPresets)
    ? customPresets.map(sanitizeCustomPreset)
    : [];

  const payload = {
    version: PRESET_STORAGE_VERSION,
    activePresetId: typeof activePresetId === 'string' && activePresetId.trim()
      ? activePresetId.trim()
      : DEFAULT_ACTIVE_PRESET_ID,
    customPresets: sanitizedCustomPresets,
  };

  try {
    window.localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(payload));
    return { success: true };
  } catch (error) {
    console.error('[LowerThirds] Failed to save preset storage:', error);
    return { success: false, error: 'quota' };
  }
};

export const removePresetStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(PRESET_STORAGE_KEY);
};
